import { InputNumber } from "primereact/inputnumber";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Pagination,
  Row,
} from "react-bootstrap";
import { useSelector } from "react-redux";
import Select from "react-select";
import { ListItem } from "../../models/ListItem";
import { RMCRoomCharge, Room } from "../../models/room.model";
import { roomService } from "../../service/room.service";
import "./ProposalRoomView.css";

interface ProposalRoomViewProps {
  propertyId: number;
  propertyName: string;
  proposalId?: number;
  clientPropertyMappingId?: number;
  rmcClientMappingId?: number;
  readOnlyMode?: boolean;
  onBack?: () => void;
  onRoomDataChange?: (
    selectedRooms: number[],
    roomCharges: Record<number, { amount: number; taxPercentage?: number }>,
    roomComplementaries: Record<number, number[]>
  ) => void;
  // optional initial data provided by parent (used to restore drafts / persisted selections)
  initialRoomData?: {
    selectedRooms?: number[];
    roomCharges?: Record<number, { amount: number; taxPercentage?: number }>;
    roomComplementaries?: Record<number, number[]>;
  };
  currencySymbol?: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onPropertyAutoSelect?: () => void;
  onPropertyAutoDeselect?: () => void;
}

const ProposalRoomView: React.FC<ProposalRoomViewProps> = ({
  propertyId,
  propertyName,
  proposalId = 0,
  clientPropertyMappingId,
  rmcClientMappingId = -1,
  readOnlyMode = false,
  onBack,
  onRoomDataChange,
  initialRoomData,
  currencySymbol = "",
  isExpanded,
  onToggleExpand,
  onPropertyAutoSelect,
  onPropertyAutoDeselect,
}) => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [complementariesList, setComplementariesList] = useState<ListItem[]>(
    []
  );
  // New state to store room-specific complementaries
  const [roomSpecificComplementaries, setRoomSpecificComplementaries] =
    useState<Record<number, ListItem[]>>({});
  const [selectedRows, setSelectedRows] = useState<Room[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rmcCharges, setRmcCharges] = useState<Record<number, RMCRoomCharge>>(
    {}
  );
  const [rmcComplementaries, setRmcComplementaries] = useState<
    Record<number, number[]>
  >({});
  const hasInitializedRef = useRef(false);

  const roomsPerPage = 8;
  const userRole = useSelector((s: any) => s.auth.userRole) ?? "";

  // Notify parent component when room data changes
  const notifyRoomDataChange = useCallback(() => {
    // don't notify parent until we've completed initial data merge
    if (!hasInitializedRef.current) return;

    if (onRoomDataChange) {
      const selectedRoomIds = selectedRows.map((room) => room.id!);
      const formattedCharges: Record<
        number,
        { amount: number; taxPercentage?: number }
      > = {};

      const filteredComplementaries: Record<number, number[]> = {};

      // Only include charges and complementaries for SELECTED rooms
      Object.entries(rmcCharges).forEach(([roomId, charge]) => {
        const roomIdNum = parseInt(roomId);
        // Only add if room is selected
        if (selectedRoomIds.includes(roomIdNum) && charge.amount > 0) {
          formattedCharges[roomIdNum] = {
            amount: charge.amount,
            taxPercentage: charge.taxPercentage || undefined,
          };
        }
      });

      // Only include complementaries for SELECTED rooms
      Object.entries(rmcComplementaries).forEach(([roomId, compIds]) => {
        const roomIdNum = parseInt(roomId);
        // Only add if room is selected
        if (selectedRoomIds.includes(roomIdNum)) {
          filteredComplementaries[roomIdNum] = compIds;
        }
      });

      onRoomDataChange(
        selectedRoomIds,
        formattedCharges,
        filteredComplementaries
      );
    }
  }, [selectedRows, rmcCharges, rmcComplementaries, onRoomDataChange]);

  // Call notification whenever relevant data changes
  useEffect(() => {
    notifyRoomDataChange();
  }, [notifyRoomDataChange]);

  const extractComplementariesFromRooms = useCallback(
    (rooms: Room[], complementariesState: Record<number, number[]>) => {
      const roomComplementariesMap: Record<number, ListItem[]> = {};

      // Extract complementaries for each room individually
      rooms.forEach((room) => {
        if (room.id && room.roomComplementaryMappings) {
          const roomComplementaries: ListItem[] = [];

          room.roomComplementaryMappings.forEach((mapping) => {
            // Access the complementary object using type assertion since it should be populated by the API
            const complementary = (mapping as any).complementary;
            if (complementary && complementary.id && complementary.name) {
              // Check if this complementary is already added to this room's list (avoid duplicates)
              if (!roomComplementaries.some((c) => c.id === complementary.id)) {
                roomComplementaries.push({
                  id: complementary.id,
                  name: complementary.name,
                });
              }
            }
          });

          roomComplementariesMap[room.id] = roomComplementaries;

          // Preselect ALL complementaries for this room if not already set
          if (
            !complementariesState[room.id] ||
            complementariesState[room.id].length === 0
          ) {
            complementariesState[room.id] = roomComplementaries.map((c) =>
              Number(c.id)
            );
          }

          console.log(`Room ${room.id} complementaries:`, roomComplementaries);
          console.log(
            `Room ${room.id} preselected IDs:`,
            complementariesState[room.id]
          );
        }
      });

      // Set the room-specific complementaries
      setRoomSpecificComplementaries(roomComplementariesMap);
      console.log("Room-specific complementaries map:", roomComplementariesMap);

      return complementariesState;
    },
    []
  );

  const fetchRooms = useCallback(async () => {
    // mark not-initialized for this property load
    hasInitializedRef.current = false;
    setIsLoading(true);
    try {
      let res: Room[] = [];

      if (
        rmcClientMappingId !== -1 &&
        (userRole === "RMCAdmin" || userRole === "RMCUser")
      ) {
        // RMC workflow - get mapped rooms
        res = await roomService.getRoomDetailsByPropertyAndClient(
          propertyId,
          rmcClientMappingId
        );
      } else {
        // Standard workflow - get all rooms for property
        res = await roomService.getByPropertyId(propertyId);
      }

      setRooms(res);

      // Load selected rooms if proposal exists
      if (proposalId && proposalId !== 0) {
        try {
          const ids = await roomService.getRoomIdsByProposal(proposalId);
          console.log("Fetched room IDs:", ids);
          setSelectedRows(res.filter((r) => ids.includes(Number(r.id))));
        } catch (error) {
          console.error("Error fetching selected room IDs:", error);
          setSelectedRows([]);
        }
      }

      // Initialize RMC charges and complementaries for each room
      const charges: Record<number, RMCRoomCharge> = {};
      const complementaries: Record<number, number[]> = {};

      res.forEach((room) => {
        if (room.id) {
          // Initialize RMC charge with base room price and tax as default
          const baseAmount = room.roomCharges?.[0]?.amount || 0;
          const baseTax = room.roomCharges?.[0]?.taxPercentage || 0;

          charges[room.id] = {
            roomId: room.id,
            proposalPropertyMappingId: clientPropertyMappingId || 0,
            amount: baseAmount,
            taxPercentage: baseTax,
          };

          // Initialize complementaries as empty - will be populated by extractComplementariesFromRooms
          complementaries[room.id] = [];
        }
      });

      // Extract complementaries and preselect all for each room
      const updatedComplementaries = extractComplementariesFromRooms(
        res,
        complementaries
      );

      // Merge any initialRoomData provided by parent (drafts / persisted selections)
      let initialSelectedIds: number[] = [];

      if (initialRoomData) {
        if (
          initialRoomData.selectedRooms &&
          initialRoomData.selectedRooms.length > 0
        ) {
          initialSelectedIds = initialRoomData.selectedRooms.map((id) =>
            Number(id)
          );
          setSelectedRows(
            res.filter((r) => r.id && initialSelectedIds.includes(Number(r.id)))
          );
        }

        if (initialRoomData.roomCharges) {
          Object.entries(initialRoomData.roomCharges).forEach(([rid, rc]) => {
            const idNum = Number(rid);
            if (charges[idNum]) {
              charges[idNum] = {
                roomId: idNum,
                proposalPropertyMappingId: clientPropertyMappingId || 0,
                amount: rc.amount || 0,
                taxPercentage: rc.taxPercentage || 0,
              };
            }
          });
        }

        if (initialRoomData.roomComplementaries) {
          Object.entries(initialRoomData.roomComplementaries).forEach(
            ([rid, comps]) => {
              const idNum = Number(rid);
              if (updatedComplementaries[idNum]) {
                updatedComplementaries[idNum] = comps || [];
              }
            }
          );
        }
      }

      setRmcCharges(charges);
      setRmcComplementaries(updatedComplementaries);

      // mark initialized after setting initial state
      hasInitializedRef.current = true;

      // Notify parent immediately with the merged initial data so parent won't overwrite drafts
      if (onRoomDataChange) {
        const formattedCharges: Record<
          number,
          { amount: number; taxPercentage?: number }
        > = {};
        Object.entries(charges).forEach(([roomId, charge]) => {
          if (charge.amount > 0) {
            formattedCharges[Number(roomId)] = {
              amount: charge.amount,
              taxPercentage: charge.taxPercentage || undefined,
            };
          }
        });

        onRoomDataChange(
          initialSelectedIds,
          formattedCharges,
          updatedComplementaries
        );
      }
    } catch (error) {
      console.error("Error fetching rooms:", error);
      setRooms([]);
      setSelectedRows([]);
      // reset initialized flag on error
      hasInitializedRef.current = false;
    } finally {
      setIsLoading(false);
    }
  }, [
    propertyId,
    proposalId,
    userRole,
    rmcClientMappingId,
    clientPropertyMappingId,
    extractComplementariesFromRooms,
  ]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const handleRoomSelection = (room: Room, isSelected: boolean) => {
    if (readOnlyMode) return;

    setSelectedRows((prev) => {
      let newSelectedRows = prev;

      // If the room is already in selectedRows and trying to select again, don't add duplicate
      if (isSelected && !prev.some((r) => r.id === room.id)) {
        // Call auto-select callback when first room is selected for this property
        if (prev.length === 0 && onPropertyAutoSelect) {
          console.log(
            `[ProposalRoomView] Property ${propertyId} - Room ${room.id} selected, auto-selecting property`
          );
          onPropertyAutoSelect();
        }
        newSelectedRows = [...prev, room];
      } else if (!isSelected) {
        newSelectedRows = prev.filter((r) => r.id !== room.id);

        // Call auto-deselect callback when last room is deselected
        if (newSelectedRows.length === 0 && onPropertyAutoDeselect) {
          console.log(
            `[ProposalRoomView] Property ${propertyId} - Last room ${room.id} deselected, auto-deselecting property`
          );
          onPropertyAutoDeselect();
        }
      }

      return newSelectedRows;
    });
  };

  const handleRmcChargeChange = (
    roomId: number,
    field: "amount" | "taxPercentage",
    value: number
  ) => {
    if (readOnlyMode) return;

    setRmcCharges((prev) => ({
      ...prev,
      [roomId]: {
        ...prev[roomId],
        [field]: value,
      },
    }));
  };

  const handleRmcComplementariesChange = (
    roomId: number,
    selectedOptions: readonly any[]
  ) => {
    if (readOnlyMode) return;

    const complementaryIds = Array.from(selectedOptions).map(
      (option) => option.value
    );
    setRmcComplementaries((prev) => ({
      ...prev,
      [roomId]: complementaryIds,
    }));
  };

  // Pagination logic
  const indexOfLastRoom = currentPage * roomsPerPage;
  const indexOfFirstRoom = indexOfLastRoom - roomsPerPage;
  const currentRooms = rooms.slice(indexOfFirstRoom, indexOfLastRoom);
  const totalPages = Math.ceil(rooms.length / roomsPerPage);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    for (let number = 1; number <= totalPages; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex justify-content-center mt-4">
        <Pagination>
          <Pagination.First
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          />
          <Pagination.Prev
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          />
          {items}
          <Pagination.Next
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          />
          <Pagination.Last
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          />
        </Pagination>
      </div>
    );
  };

  const getMainImage = (room: Room) => {
    if (!room.roomImages || room.roomImages.length === 0) {
      return "data:image/svg+xml,%3csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100' height='100' fill='%23f8f9fa'/%3e%3ctext x='50%25' y='50%25' font-size='14' text-anchor='middle' dy='.3em' fill='%23999'%3eNo Image%3c/text%3e%3c/svg%3e";
    }

    const mainImage = room.roomImages.find((img) => img.isMainImage);
    return (
      mainImage?.imageUrl ||
      room.roomImages[0]?.imageUrl ||
      "data:image/svg+xml,%3csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100' height='100' fill='%23f8f9fa'/%3e%3ctext x='50%25' y='50%25' font-size='14' text-anchor='middle' dy='.3em' fill='%23999'%3eNo Image%3c/text%3e%3c/svg%3e"
    );
  };

  const isRoomSelected = (room: Room) =>
    selectedRows.some((r) => r.id === room.id);

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading rooms...</p>
      </div>
    );
  }

  return (
    <div className="proposal-room-view">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <div className="section-title mb-1">
            <i className="pi pi-th-large" style={{ fontSize: ".85rem" }}></i>
            <span>{propertyName} – Rooms</span>
          </div>
          <div
            className="text-muted"
            style={{ fontSize: ".65rem", letterSpacing: ".4px" }}
          >
            {rooms.length} room{rooms.length !== 1 ? "s" : ""} •{" "}
            {selectedRows.length} selected
          </div>
        </div>
        {onBack && (
          <Button variant="outline-secondary" size="sm" onClick={onBack}>
            <i className="bi bi-arrow-left me-1"></i>
            Back
          </Button>
        )}
      </div>

      {/* RMC Configuration Info - Show when no rooms selected */}
      {/* Info banner */}
      {/* Removed per request: general RMC configuration banner */}

      {/* Room List */}
      <Row className="g-3">
        {currentRooms.map((room) => {
          const isSelected = isRoomSelected(room);
          const roomId = room.id!;
          const rmcCharge = rmcCharges[roomId];
          const roomComplementaries = rmcComplementaries[roomId] || [];

          return (
            <Col key={room.id} md={12} lg={6} xl={4}>
              <Card
                className={`room-card h-100 ${
                  isSelected ? "selected border-primary" : ""
                }`}
                style={{ cursor: "default" }}
              >
                {/* Room Image */}
                <div className="room-image">
                  <img
                    src={getMainImage(room)}
                    alt={room.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      e.currentTarget.src =
                        "data:image/svg+xml,%3csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100' height='100' fill='%23f8f9fa'/%3e%3ctext x='50%25' y='50%25' font-size='14' text-anchor='middle' dy='.3em' fill='%23999'%3eNo Image%3c/text%3e%3c/svg%3e";
                      e.currentTarget.onerror = null;
                    }}
                  />

                  {/* Selection Checkbox */}
                  <div className="selection-checkbox">
                    <Form.Check
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleRoomSelection(room, e.target.checked);
                      }}
                      disabled={readOnlyMode}
                    />
                  </div>

                  {/* Selected Badge */}
                  {isSelected && (
                    <div className="selected-badge">
                      <Badge bg="primary">Selected</Badge>
                    </div>
                  )}
                </div>

                <Card.Body className="room-info">
                  {/* Room Basic Info */}
                  <div className="mb-3">
                    <h6 className="card-title mb-2">{room.name}</h6>
                    <p
                      className="card-text room-description-clamp mb-2"
                      title={room.description || ""}
                    >
                      {room.description}
                    </p>

                    <div className="room-meta">
                      <div className="room-meta-item">
                        <i className="pi pi-expand" />
                        {room.area || 0} sq ft
                      </div>
                      <div className="room-meta-item">
                        <i className="pi pi-th-large" />
                        {room.roomType?.name || "N/A"}
                      </div>
                      <div className="room-meta-item">
                        <i className="pi pi-home" />
                        {room.numberOfBedrooms || 0} bed
                        {(room.numberOfBedrooms || 0) !== 1 ? "s" : ""}
                      </div>
                      <div className="room-meta-item">
                        <i className="pi pi-sliders-h" />
                        {room.numberOfBathrooms || 0} bath
                        {(room.numberOfBathrooms || 0) !== 1 ? "s" : ""}
                      </div>
                    </div>

                    {/* Base (standard) Room Charge - read-only */}
                    {room.roomCharges && room.roomCharges.length > 0 && (
                      <div
                        className="room-base-charge mt-2"
                        title="Base room charge (standard)"
                      >
                        <i className="bi bi-currency-rupee me-1"></i>

                        <span>
                          {Number(room.roomCharges[0].amount).toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}
                        </span>
                        {room.roomCharges[0].taxPercentage != null && (
                          <span className="text-muted ms-2 small">
                            Tax {room.roomCharges[0].taxPercentage}%
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Admin instruction for unselected rooms */}
                  {/* Removed selection instruction per request */}

                  {/* RMC Fields - Show for Admin (always visible, disabled if not selected), or for all users when in read-only */}
                  {((!readOnlyMode &&
                    userRole === "Administrator" &&
                    isSelected) ||
                    (readOnlyMode && isSelected)) && (
                    <div className="rmc-config">
                      <h6>
                        <i className="bi bi-gear me-1"></i>
                        RMC Specific Configuration
                        {readOnlyMode && (
                          <small className="text-muted ms-1">(View-only)</small>
                        )}
                      </h6>

                      {/* RMC Charges - Show for Admin editing or read-only display */}
                      <Row className="mb-2">
                        <Col xs={12} md={6}>
                          <Form.Label className="small">
                            RMC Charges
                            {/* {currencySymbol ? (
                              <span className="ms-2 text-muted small">{currencySymbol}</span>
                            ) : (
                              <span className="ms-2 text-muted small"><i className="bi bi-currency-rupee"></i></span>
                            )} */}
                          </Form.Label>
                          <div className="input-group input-group-sm">
                            {currencySymbol ? (
                              <span className="input-group-text">
                                {currencySymbol}
                              </span>
                            ) : (
                              <span className="input-group-text">
                                <i className="bi bi-currency-rupee"></i>
                              </span>
                            )}
                            <Form.Control
                              type="number"
                              size="sm"
                              value={rmcCharge?.amount || ""}
                              onChange={(e) =>
                                !readOnlyMode &&
                                isSelected &&
                                handleRmcChargeChange(
                                  roomId,
                                  "amount",
                                  +e.target.value
                                )
                              }
                              placeholder="0.00"
                              className="form-control-sm"
                              readOnly={readOnlyMode}
                              disabled={!readOnlyMode && !isSelected}
                            />
                          </div>
                        </Col>

                        <Col xs={12} md={6}>
                          <Form.Label className="small">RMC Tax %</Form.Label>
                          <InputNumber
                            value={rmcCharge?.taxPercentage ?? null}
                            onValueChange={(e) =>
                              !readOnlyMode &&
                              isSelected &&
                              handleRmcChargeChange(
                                roomId,
                                "taxPercentage",
                                e.value ?? 0
                              )
                            }
                            min={0}
                            max={100}
                            placeholder="0.00"
                            inputClassName="form-control-sm"
                            inputStyle={{ width: "80px" }}
                            readOnly={readOnlyMode}
                            disabled={!readOnlyMode && !isSelected}
                            mode="decimal"
                            minFractionDigits={0}
                            maxFractionDigits={2}
                          />
                        </Col>
                      </Row>

                      {/* RMC Complementaries */}
                      <div className="mb-2">
                        <Form.Label className="small">
                          RMC Complementaries
                        </Form.Label>
                        {readOnlyMode ? (
                          <div className="d-flex flex-wrap gap-1">
                            {(roomSpecificComplementaries[roomId] || [])
                              .filter((c) =>
                                roomComplementaries.includes(Number(c.id))
                              )
                              .map((complementary, idx) => (
                                <Badge
                                  key={idx}
                                  bg="secondary"
                                  className="small"
                                >
                                  {complementary.name}
                                </Badge>
                              ))}
                            {(!roomComplementaries ||
                              roomComplementaries.length === 0) && (
                              <small className="text-muted">
                                No complementaries configured
                              </small>
                            )}
                          </div>
                        ) : (
                          <Select
                            isMulti
                            isDisabled={!isSelected}
                            value={(roomSpecificComplementaries[roomId] || [])
                              .filter((c) =>
                                roomComplementaries.includes(Number(c.id))
                              )
                              .map((c) => ({
                                label: c.name,
                                value: Number(c.id),
                              }))}
                            options={(
                              roomSpecificComplementaries[roomId] || []
                            ).map((c) => ({
                              label: c.name,
                              value: Number(c.id),
                            }))}
                            onChange={(selected) =>
                              isSelected &&
                              handleRmcComplementariesChange(
                                roomId,
                                selected || []
                              )
                            }
                            placeholder={
                              roomSpecificComplementaries[roomId]?.length
                                ? "Select complementaries..."
                                : "No complementaries available for this room"
                            }
                            className="react-select-sm"
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            menuPlacement="auto"
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 1000,
                                position: "absolute",
                              }),
                            }}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Empty State */}
      {rooms.length === 0 && (
        <div className="empty-state">
          <i className="bi bi-house-door"></i>
          <h5 className="mt-3">No rooms found</h5>
          <p>There are no rooms available for this property.</p>
        </div>
      )}

      {/* Pagination */}
      {renderPagination()}
    </div>
  );
};

export default ProposalRoomView;
